import { useWizard } from '../../context/WizardContext';
import { motion } from 'framer-motion';
import { User, Mail, Phone, PenLine, Loader2, CheckCircle, Shield } from 'lucide-react';
import { useState } from 'react';
import { createPreview } from '../../lib/api';

export const Step3Contato = ({ onPreviewComplete }) => {
  const { data, updateData, prevStep, isSubmitting, setIsSubmitting, setPreview, setError } = useWizard();
  const [localError, setLocalError] = useState('');

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidWhatsapp = (phone) => phone.replace(/\D/g, '').length >= 8;

  const isValid = data.name.length >= 2 &&
                  isValidEmail(data.email) &&
                  isValidWhatsapp(data.whatsapp);

  const handleSubmit = async () => {
    if (!isValid || isSubmitting) return;
    setIsSubmitting(true);
    setLocalError('');

    try {
      const wizardData = {
        country_now: data.country_now,
        nationality: data.nationality,
        destination_country: data.destination_country,
        city: data.city,
        inside_spain: data.inside_spain,
        inside_eu: data.inside_eu,
        goal: data.goal,
        family: data.family,
        finance: data.finance,
        free_text: data.free_text,
        name: data.name,
        email: data.email,
        whatsapp: data.whatsapp
      };

      const result = await createPreview(wizardData);
      setPreview(result);
      if (onPreviewComplete) onPreviewComplete(result);
    } catch (err) {
      console.error('Preview error:', err);
      setLocalError('Erro ao processar. Tente novamente.');
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-4"
    >
      {/* Name + Email + WhatsApp row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="flex items-center gap-1 text-sm text-gray-400 mb-1">
            <User className="w-3.5 h-3.5 text-[#C9A24A]" />
            Nome completo
          </label>
          <input
            data-testid="name-input"
            type="text"
            value={data.name}
            onChange={(e) => updateData({ name: e.target.value })}
            placeholder="Seu nome"
            className="w-full input-dark"
          />
        </div>
        <div>
          <label className="flex items-center gap-1 text-sm text-gray-400 mb-1">
            <Mail className="w-3.5 h-3.5 text-[#C9A24A]" />
            E-mail
          </label>
          <input
            data-testid="email-input"
            type="email"
            value={data.email}
            onChange={(e) => updateData({ email: e.target.value })}
            placeholder="seu@email.com"
            className="w-full input-dark"
          />
        </div>
      </div>

      <div>
        <label className="flex items-center gap-1 text-sm text-gray-400 mb-1">
          <Phone className="w-3.5 h-3.5 text-[#C9A24A]" />
          WhatsApp
        </label>
        <input
          data-testid="whatsapp-input"
          type="tel"
          value={data.whatsapp}
          onChange={(e) => updateData({ whatsapp: e.target.value })}
          placeholder="+55 11 99999-9999"
          className="w-full input-dark"
        />
      </div>

      {/* Free text - optional */}
      <div>
        <label className="flex items-center gap-1 text-sm text-gray-400 mb-1">
          <PenLine className="w-3.5 h-3.5 text-[#C9A24A]" />
          Algo mais sobre seu caso? <span className="text-gray-600">(opcional)</span>
        </label>
        <textarea
          data-testid="free-text-input"
          value={data.free_text}
          onChange={(e) => updateData({ free_text: e.target.value })}
          placeholder="Ex: Ja tenho passaporte, quero ir com minha esposa..."
          className="w-full h-20 input-dark resize-none text-sm"
        />
      </div>

      {/* Guarantee badge */}
      <div className="flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-lg p-3">
        <Shield className="w-5 h-5 text-green-400 flex-shrink-0" />
        <p className="text-xs text-green-300">
          <strong>Garantia de 7 dias:</strong> se nao ficar satisfeito com o relatorio, devolvemos 100% do valor.
        </p>
      </div>

      {localError && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm">
          {localError}
        </div>
      )}

      {/* Navigation */}
      <div className="flex gap-3 pt-2">
        <button type="button" data-testid="step3-back" onClick={prevStep} disabled={isSubmitting} className="flex-1 btn-outline-gold">
          Voltar
        </button>
        <button type="button" data-testid="submit-wizard" onClick={handleSubmit} disabled={!isValid || isSubmitting}
          className={`flex-1 btn-gold flex items-center justify-center gap-2 ${(!isValid || isSubmitting) ? 'opacity-50 cursor-not-allowed' : ''}`}>
          {isSubmitting ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Calculando Score...</>
          ) : (
            <><CheckCircle className="w-4 h-4" /> Gerar Diagnostico e Score</>
          )}
        </button>
      </div>
    </motion.div>
  );
};

export default Step3Contato;
